import React from 'react';
import { 
  Menu, ChevronLeft, Hash, MicOff, Slash, Trash2, Phone, Pin, 
  X, Clock, Search, Activity, MessageSquare, Smile, 
  Image as ImageIcon, Square, Mic 
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { NeuralMessageBubble } from '../NeuralMessageBubble';
import { UserBadge } from '../../core/identity/IdentityRenderer';
import { Message, Channel } from '../../types/chat';

export interface ChatAreaProps {
  forcedChannelId?: string;
  activeChannelId: string | null;
  channels: Channel[];
  activeVault: any;
  activeContact: any;
  activeContactId: string | null;
  setIsTopBarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isTopBarExpanded: boolean;
  setMobileView: (view: 'sidebar' | 'chat') => void;
  setViewingProfileId: (id: string | null) => void;
  handleUserContextMenu: (e: React.MouseEvent, userId: string) => void;
  getUserFontStyle: (userId: string) => any;
  getUserRoleColor: (userId: string) => any;
  entityResolver: any;
  migrateLegacyBadge: (badge: any) => any;
  mutedUsers: string[];
  blockedUsers: string[];
  toggleBlockUser: (userId: string) => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  startCall: () => void;
  showPins: boolean;
  setShowPins: React.Dispatch<React.SetStateAction<boolean>>;
  showTimerSettings: boolean;
  setShowTimerSettings: React.Dispatch<React.SetStateAction<boolean>>;
  expiryTime: number;
  setExpiryTime: (val: number) => void;
  isCallActive: boolean;
  showSideChat: boolean;
  setShowSideChat: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveVault: (vault: any) => void;
  scrollToMessage: (id: string) => void;
  chatScrollerRef: React.RefObject<HTMLDivElement | null>;
  messagesLoading: boolean;
  displayedMessages: Message[];
  readReceipts: Record<string, string>;
  addReaction: (msgId: string, emoji: string) => void;
  setPickerMode: (mode: 'message' | 'reaction') => void;
  setPickerTargetId: (id: string | null) => void;
  showEmojiPicker: boolean;
  setShowEmojiPicker: React.Dispatch<React.SetStateAction<boolean>>;
  typingUsers: Record<string, boolean>;
  contacts: any[];
  pickerTab: 'gif' | 'emotes' | 'emoji';
  setPickerTab: (tab: 'gif' | 'emotes' | 'emoji') => void;
  handleEmojiClick: (emojiData: any) => void;
  onPickerItemClick: (text: string) => void;
  emoteSearch: string;
  setEmoteSearch: (val: string) => void;
  customEmotes: any[];
  sendMessage: (e?: React.FormEvent, directText?: string) => void;
  handleFileUpload: (file: File) => void;
  imageInputRef: React.RefObject<HTMLInputElement | null>;
  handleToggleRecording: () => void;
  isRecording: boolean;
  richInputRef: React.RefObject<HTMLDivElement | null>;
  setInputValue: (val: string) => void;
  getMarkdownFromRichInput: () => string;
  handleTyping: () => void;
  currentUser: any;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  forcedChannelId,
  activeChannelId,
  channels,
  activeVault,
  activeContact,
  activeContactId,
  setIsTopBarExpanded,
  isTopBarExpanded,
  setMobileView,
  setViewingProfileId,
  handleUserContextMenu,
  getUserFontStyle,
  getUserRoleColor,
  entityResolver,
  migrateLegacyBadge,
  mutedUsers,
  blockedUsers,
  toggleBlockUser,
  messages,
  setMessages,
  startCall,
  showPins,
  setShowPins,
  showTimerSettings,
  setShowTimerSettings,
  expiryTime,
  setExpiryTime,
  isCallActive,
  showSideChat,
  setShowSideChat,
  setActiveVault,
  scrollToMessage,
  chatScrollerRef,
  messagesLoading,
  displayedMessages,
  readReceipts,
  addReaction,
  setPickerMode,
  setPickerTargetId,
  showEmojiPicker,
  setShowEmojiPicker,
  typingUsers,
  contacts,
  pickerTab,
  setPickerTab,
  handleEmojiClick,
  onPickerItemClick,
  emoteSearch,
  setEmoteSearch,
  customEmotes,
  sendMessage,
  handleFileUpload,
  imageInputRef,
  attachments = [],
  setAttachments,
  handleToggleRecording,
  isRecording,
  richInputRef,
  setInputValue,
  getMarkdownFromRichInput,
  handleTyping,
  currentUser,
}) => {
  const effectiveChannelId = forcedChannelId || activeChannelId || 'general';
  const channelName = channels.find(c => c.id === effectiveChannelId)?.name || effectiveChannelId;

  return (
    <>
      <div className="msg-header" onClick={() => setIsTopBarExpanded(true)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsTopBarExpanded(!isTopBarExpanded);
            }}
            className="md:hidden p-2 -ml-2 text-[var(--neon)] hover:bg-white/5 rounded-lg transition-colors"
            title="Open Servers"
          >
            <Menu size={20} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setMobileView('sidebar');
            }}
            className="md:hidden p-2 text-[var(--neon)] hover:bg-white/5 rounded-lg transition-colors"
            title="Back to channels"
          >
            <ChevronLeft size={20} />
          </button>
          {activeVault ? (
            <div className="flex items-center gap-2 min-w-0">
              <Hash size={18} className="text-white/40 shrink-0" />
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 800 }} className="truncate max-w-[100px] md:max-w-none">{channelName}</h4>
              <div className="w-[1px] h-4 bg-white/10 mx-2 shrink-0" />
              <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest truncate max-w-[80px] md:max-w-none">{activeVault.name}</span>
            </div>
          ) : activeContact ? (
            <>
              <div 
                className="avatar cursor-pointer shrink-0" 
                style={{ width: '28px', height: '28px' }}
                onClick={() => setViewingProfileId(activeContact.id)}
                onContextMenu={(e) => handleUserContextMenu(e, activeContact.id)}
              >
                {activeContact.type === 'human' ? (
                  <img src={activeContact.avatar || undefined} alt="avatar" />
                ) : (
                  <div className={`w-full h-full rounded-full bg-${activeContact.botColor}-500/20 border border-${activeContact.botColor}-500/50 flex items-center justify-center`}>
                    <span className={`text-${activeContact.botColor}-400 font-bold text-[10px]`}>{activeContact.botBadge}</span>
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h4 
                  style={{ margin: 0, fontSize: '13px', letterSpacing: '0.5px', ...getUserFontStyle(activeContact.id) }}
                  className="cursor-pointer hover:underline flex items-center gap-2 truncate max-w-[120px] md:max-w-none"
                  onClick={() => setViewingProfileId(activeContact.id)}
                  onContextMenu={(e) => handleUserContextMenu(e, activeContact.id)}
                >
                  {activeContact.name}
                  <UserBadge customization={migrateLegacyBadge(entityResolver.resolveUser(activeContact.id)?.customization)} />
                  {mutedUsers.includes(activeContact.id) && <MicOff size={12} className="text-red-500 shrink-0" />}
                  {blockedUsers.includes(activeContact.id) && <Slash size={12} className="text-red-600 shrink-0" />}
                </h4>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '9px', color: blockedUsers.includes(activeContact.id) ? '#ef4444' : '#2bf0a2', fontFamily: 'var(--mono)', letterSpacing: '1px' }}>
                    {blockedUsers.includes(activeContact.id) ? 'BLOCKED' : 'SECURE'}
                  </span>
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="header-tools relative flex items-center gap-1">
          {effectiveChannelId === 'global' && (
            <span className="tool-icon" onClick={() => setMessages([])} title="Clear Global Feed">
              <Trash2 size={20} className="hover:text-[var(--neon)] transition-colors" />
            </span>
          )}
          {!activeVault && (
            <>
              <span className="tool-icon" onClick={startCall} title="Start Voice/Video Call"><Phone size={20} /></span>
              <span className="tool-icon" onClick={() => setShowPins(!showPins)}><Pin size={20} className={showPins ? "text-[var(--neon)]" : ""} /></span>
            </>
          )}
          <span className="tool-icon" onClick={() => setShowTimerSettings(!showTimerSettings)}><Clock size={20} className={showTimerSettings ? "text-[var(--neon)]" : ""} /></span>
          <span className="tool-icon"><Search size={20} /></span>
      
      {showPins && (
        <div className="absolute top-8 right-0 w-64 bg-[var(--bg-panel)] theme-panel border border-white/10 rounded-xl p-3 z-30 shadow-xl">
          <h3 className="text-xs font-bold uppercase mb-2 border-b border-white/10 pb-1">Pinned Messages</h3>
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {messages.filter(m => m.pinned).length === 0 && <span className="text-xs text-white/30">No pins yet.</span>}
            {messages.filter(m => m.pinned).map(msg => (
              <div key={msg.id} onClick={() => scrollToMessage(msg.id)} className="text-xs p-2 bg-white/5 rounded cursor-pointer hover:bg-white/10 truncate">
                {msg.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {showTimerSettings && (
        <div className="absolute top-8 right-8 w-48 bg-[var(--bg1)] backdrop-blur-xl border border-white/10 rounded-xl p-0 z-30 shadow-2xl overflow-hidden">
          <div className="bg-[var(--neon)]/10 border-b border-white/10 p-3 flex items-center gap-2">
            <Activity size={14} className="text-[var(--neon)] animate-pulse" />
            <h3 className="text-[10px] font-black tracking-widest uppercase text-white">Disappearing</h3>
          </div>
          <div className="flex flex-col max-h-60 overflow-y-auto">
            {[
              { label: 'Off', val: 0 },
              { label: '5 Seconds', val: 5000 },
              { label: '10 Seconds', val: 10000 },
              { label: '30 Seconds', val: 30000 },
              { label: '1 Minute', val: 60000 },
              { label: '1 Hour', val: 3600000 },
              { label: '8 Hours', val: 28800000 },
            ].map(opt => (
              <button 
                key={opt.label}
                onClick={() => { setExpiryTime(opt.val); setShowTimerSettings(false); }}
                className={`text-xs font-black tracking-wider uppercase text-left px-5 py-4 transition-all ${expiryTime === opt.val ? 'bg-[var(--neon)] text-[var(--bg1)]' : 'text-white/60 hover:bg-white/10 hover:text-white'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>

  <div className="chat-scroller" ref={chatScrollerRef}>
    {messagesLoading ? (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--neon)] border-t-transparent rounded-full animate-spin" />
      </div>
    ) : displayedMessages.length === 0 ? (
      <div className="flex-1 flex flex-col items-center justify-start pt-24 opacity-40 h-full">
        <MessageSquare size={48} className="mb-4" />
        <p className="text-xs font-bold uppercase tracking-widest">No messages yet</p>
        <p className="text-[10px] mt-2 tracking-widest">START THE CONVERSATION</p>
      </div>
    ) : displayedMessages.map((msg) => (
      <NeuralMessageBubble 
        key={msg.id}
        msg={msg}
        isSent={msg.type === 'sent'}
        readReceipts={readReceipts}
        activeVault={activeVault}
        onReaction={addReaction}
        onMoreReactions={(id: string) => { setPickerMode('reaction'); setPickerTargetId(id); setShowEmojiPicker(true); }}
        onUserClick={handleUserContextMenu}
        currentUser={currentUser}
        senderFontStyle={getUserFontStyle(msg.senderId)}
        roleColor={getUserRoleColor(msg.senderId)}
      />
    ))}
  </div>

  {Object.keys(typingUsers).length > 0 && (
    <div className="typing-indicator">
      <div className="typing-dots">
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
      </div>
      <span>
        {Object.keys(typingUsers).map(uid => contacts.find(c => c.id === uid)?.name || 'Someone').join(', ')} is typing...
      </span>
    </div>
  )}

  <div className="input-box relative">
    {blockedUsers.includes(activeContactId || '') && (
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-30 flex items-center justify-center rounded-xl border border-red-500/20">
        <div className="flex items-center gap-3 text-red-500">
          <Slash size={16} />
          <span className="text-xs font-bold uppercase tracking-widest">You have blocked this user</span>
          <button 
            onClick={() => toggleBlockUser(activeContactId!)}
            className="ml-2 px-3 py-1 bg-red-500 text-white text-[10px] font-bold rounded hover:bg-red-600 transition-colors"
          >
            UNBLOCK
          </button>
        </div>
      </div>
    )}
    {showEmojiPicker && (
      <div className="fixed md:absolute bottom-16 inset-x-2 md:left-4 z-50 md:z-20 w-auto md:w-80 max-w-sm mx-auto md:mx-0 bg-[var(--bg1)]/95 backdrop-blur-xl theme-panel border border-white/10 rounded-2xl md:rounded-xl shadow-2xl overflow-hidden flex flex-col h-[55vh] md:h-[450px]">
        <div className="flex bg-black/40 p-2 gap-2 border-b border-white/10">
          <button onClick={() => setPickerTab('gif')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'gif' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>GIFs</button>
          <button onClick={() => setPickerTab('emotes')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'emotes' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>EMOTES</button>
          <button onClick={() => setPickerTab('emoji')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'emoji' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>EMOJI</button>
        </div>
        <div className="flex-grow overflow-hidden">
          {pickerTab === 'emoji' && <EmojiPicker theme={'dark' as any} onEmojiClick={handleEmojiClick} width="100%" height="100%" />}
          {pickerTab === 'gif' && (
            <div className="p-2 overflow-y-auto h-full grid grid-cols-2 gap-2">
               {['https://media.giphy.com/media/3o7TKSjRrfIPjeiKKc/giphy.gif', 'https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif', 'https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif', 'https://media.giphy.com/media/26BRv0ThflsHCqDrG/giphy.gif'].map((url, i) => (
                 <img key={i} src={url} className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80" onClick={() => onPickerItemClick(`![gif](${url})`)} />
               ))}
            </div>
          )}
          {pickerTab === 'emotes' && (
            <div className="p-3 flex flex-col h-full overflow-hidden">
               <div className="mb-3 px-1 flex items-center justify-between">
                 <h4 className="text-xs font-bold text-white/30 uppercase">Custom Emotes</h4>
                 <div className="relative flex-1 ml-4">
                   <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30" />
                   <input type="text" placeholder="Search emotes..." className="w-full bg-white/5 border border-white/10 rounded-md py-1.5 pl-8 pr-2 text-xs text-white outline-none focus:border-[var(--neon)] transition-colors" value={emoteSearch} onChange={(e) => setEmoteSearch(e.target.value)} />
                 </div>
               </div>
               <div className="flex-grow overflow-y-auto pr-1">
                 <div className="grid grid-cols-5 gap-2 w-full">
                    {customEmotes.length > 0 ? customEmotes.filter(e => e.name.toLowerCase().includes(emoteSearch.toLowerCase())).map((emote, i) => (
                          <div key={i} className="aspect-square bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 cursor-pointer transition-colors group p-1.5 overflow-hidden" onClick={() => onPickerItemClick(`![emote](${emote.url})`)} title={emote.name}>
                             <img src={emote.url} className="w-full h-full object-contain" alt={emote.name} referrerPolicy="no-referrer" />
                          </div>
                        )) : Array.from({ length: 20 }).map((_, i) => (
                        <div key={i} className="aspect-square bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 cursor-pointer transition-colors group"><Smile size={16} className="text-white/20 group-hover:text-[var(--neon)]" /></div>
                      ))}
                 </div>
               </div>
            </div>
          )}
        </div>
      </div>
    )}
    <form 
      className="input-wrapper" 
      onSubmit={sendMessage}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const file = e.dataTransfer.files?.[0]; if (file) handleFileUpload(file); }}
    >
      <button type="button" onClick={() => { setPickerMode('message'); setShowEmojiPicker(!showEmojiPicker); }} className="text-white/50 hover:text-white p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg hover:bg-white/5 transition-all"><Smile size={20} /></button>
      <button type="button" onClick={() => imageInputRef.current?.click()} className="text-white/50 hover:text-white p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg hover:bg-white/5 transition-all" title="Upload Media"><ImageIcon size={20} /></button>
      <input type="file" ref={imageInputRef} className="hidden" accept="image/*,video/*,audio/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload(file); }} />
      <button 
        type="button" 
        onClick={handleToggleRecording} 
        className={`p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg transition-all ${isRecording ? 'text-red-500 animate-pulse bg-red-500/10' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
        title={isRecording ? "Stop Recording" : "Record Voice Message"}
      >
        {isRecording ? <Square size={20} /> : <Mic size={20} />}
      </button>
      <div className="rich-input" contentEditable ref={richInputRef} data-placeholder={`Send Message... (Drop media here)`} onInput={() => { setInputValue(getMarkdownFromRichInput()); handleTyping(); }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} onPaste={(e) => {
        const fileList = Array.from(e.clipboardData?.files || []);
        let imageFile = fileList.find((f) => f.type.startsWith('image/'));
        if (!imageFile) {
          const items = e.clipboardData?.items;
          if (items) {
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.indexOf('image') !== -1) {
                imageFile = items[i].getAsFile() || undefined;
                if (imageFile) break;
              }
            }
          }
        }
        if (imageFile) {
          e.preventDefault();
          handleFileUpload(imageFile);
        }
      }} />
      <button type="submit" className="send-btn">SEND</button>
    </form>
  </div>
  </>
  );
};

export default ChatArea;
